import { currencySchema, oneTimePurchaseOptionsSchema, providerResultSchema } from "../../../payment/shared.mjs";
import { orderSchema } from "../order/schemas.mjs";
import { z } from "zod";
import { oc } from "@orpc/contract";
//#region src/api/orpc/internal/kotoneOrder.ts
/**
* Mirror of `ChargeMode` from the server's
* `src/service/helper/payment/kotoneOrder/types.ts`. The three values are
* inlined as a zod enum literal so the contract package stays free of
* server-only imports; the canonical definition stays on the server
* alongside the kotoneOrder service code.
*/
const chargeModeSchema = z.enum([
	"interactive",
	"subscribe",
	"save_card"
]);
/** Coerces HTTP string/number to bigint for input schemas */
const idInputSchema = z.coerce.bigint();
const createKotoneOrderInputSchema = oneTimePurchaseOptionsSchema.extend({
	userId: idInputSchema,
	amountMicros: z.coerce.bigint(),
	currency: currencySchema,
	productName: z.string(),
	subscriptionProductName: z.string().optional(),
	itemId: z.string(),
	ip: z.union([z.ipv4(), z.ipv6()]).optional(),
	callbackUrl: z.url(),
	callbackMeta: z.record(z.string(), z.unknown()),
	chargeMode: chargeModeSchema.default("interactive")
});
const createKotoneOrderOutputSchema = z.object({
	orderId: z.coerce.bigint(),
	providerResult: providerResultSchema.optional()
});
const getKotoneOrderInputSchema = z.object({ orderId: idInputSchema });
const cancelKotoneOrderInputSchema = z.object({ orderId: idInputSchema });
const kotoneOrderContract = oc.prefix("/internal/kotone/order").tag("KotoneOrder").router({
	create: oc.route({
		method: "POST",
		path: "/",
		summary: "Create a one-time purchase order for Kotone"
	}).input(createKotoneOrderInputSchema).output(createKotoneOrderOutputSchema).errors({
		INVALID_PROVIDER: {
			status: 400,
			message: "Unsupported provider"
		},
		AUTO_CHARGE_PROVIDER_NOT_SUPPORTED: {
			status: 400,
			message: "chargeMode subscribe/save_card is only supported for onerway provider"
		}
	}),
	get: oc.route({
		method: "GET",
		path: "/{orderId}",
		summary: "Get Kotone order by ID"
	}).input(getKotoneOrderInputSchema).output(orderSchema).errors({ NOT_FOUND: {
		status: 404,
		message: "Order not found"
	} }),
	cancel: oc.route({
		method: "POST",
		path: "/{orderId}/cancel",
		summary: "Cancel a Kotone order"
	}).input(cancelKotoneOrderInputSchema).errors({
		NOT_FOUND: {
			status: 404,
			message: "Order not found"
		},
		BAD_REQUEST: {
			status: 400,
			message: "Order is not in a cancellable state"
		}
	}),
	complete: oc.route({
		method: "POST",
		path: "/{orderId}/complete",
		summary: "Mark a Kotone order as completed (Agent confirms fulfillment)"
	}).input(getKotoneOrderInputSchema).errors({
		NOT_FOUND: {
			status: 404,
			message: "Order not found"
		},
		BAD_REQUEST: {
			status: 400,
			message: "Order is not in paid status"
		}
	}),
	confirmRefund: oc.route({
		method: "POST",
		path: "/{orderId}/confirm-refund",
		summary: "Mark a Kotone order as refunded (Agent confirms benefit revocation)"
	}).input(getKotoneOrderInputSchema).errors({
		NOT_FOUND: {
			status: 404,
			message: "Order not found"
		},
		BAD_REQUEST: {
			status: 400,
			message: "Order is not in refund_pending status"
		}
	})
});
//#endregion
export { cancelKotoneOrderInputSchema, createKotoneOrderInputSchema, getKotoneOrderInputSchema, kotoneOrderContract };
